// Display derivation for the full-history ledger (`useLedger`).
//
// The ledger replaces the windowed cost-basis guesswork with server-side
// facts, but the same honesty rules apply on the way to the screen:
//
//  * Unrealized PnL is computed ONLY over the exactly-priced portion — the
//    tokens bought for frBTC, whose cost is sats read off chain. Minted and
//    transferred-in tokens have no exact cost; they appear in the disclosure
//    line (with their receipt-date valuation when one exists), never inside
//    the PnL number.
//  * Every figure that is a floor says so: `coverage: 'truncated'` and
//    `reconciled: false` become visible caveats, not silent omissions.

import type { LedgerToken } from './useLedger'

export interface LedgerPosition {
	/// Exact sats that entered this token's frBTC-priced buys.
	investedSats: bigint
	/// Exact-sats basis still attached to what remains after sells/transfers
	/// removed their proportional share (average-cost).
	remainingBasisSats: bigint
	/// Sats per whole token over the exactly-priced buys, or null when none.
	avgEntrySatsPerToken: number | null
	/// Average-cost realized PnL in sats over completed frBTC round trips.
	realizedSats: bigint
	/// Unrealized PnL in sats over the exactly-priced portion of the CURRENT
	/// balance, at the live mid — null when the position can't be priced.
	unrealizedSats: bigint | null
	/// Base units of the current balance the unrealized figure covers.
	trackedQty: bigint
	/// The split, spelled out: where the rest of the balance came from.
	disclosure: string[]
	reconciled: boolean | null
	truncated: boolean
	/// Receipt-date USD valuation of minted/transferred-in lots, with the
	/// quantity it covers — labeled market valuation, never sats.
	valuedUsdAcquired: number | null
	valuedQty: bigint
	unvaluedQty: bigint
}

function big(s: string | null | undefined): bigint {
	if (!s) {
		return 0n
	}
	try {
		return BigInt(s)
	} catch {
		return 0n
	}
}

/// Whole tokens, compactly, for the disclosure line (8dp base units).
function whole(qty: bigint): string {
	const tokens = Number(qty) / 1e8
	if (tokens >= 1_000_000) {
		return `${(tokens / 1_000_000).toFixed(1)}M`
	}
	if (tokens >= 1_000) {
		return `${(tokens / 1_000).toFixed(1)}K`
	}
	if (tokens >= 1) {
		return tokens.toLocaleString('en-US', { maximumFractionDigits: 2 })
	}
	return tokens.toLocaleString('en-US', { maximumSignificantDigits: 3 })
}

/// Derive one position's display numbers from its ledger fold.
///
/// `raw` is the live balance in base units (the figure the row shows) and
/// `satsNow` its value in frBTC base units at the live mid, or null when no
/// pool prices it — both already computed by the portfolio.
export function ledgerPosition(
	t: LedgerToken,
	raw: bigint,
	satsNow: bigint | null
): LedgerPosition {
	const investedSats = big(t.invested_sats_exact)
	const remainingBasisSats = big(t.remaining_basis_sats)
	const qtyBoughtExact = big(t.qty_bought_exact)
	// The exactly-priced portion of what is HELD now: never more than the
	// balance (sells shrank it), never more than was exactly bought.
	const trackedQty = raw < qtyBoughtExact ? raw : qtyBoughtExact
	// Its value at the live mid, proportional to the whole balance's value —
	// same mid, same rounding, so tracked === raw reproduces satsNow exactly.
	let unrealizedSats: bigint | null = null
	if (satsNow !== null && raw > 0n && trackedQty > 0n) {
		const trackedNow = (satsNow * trackedQty) / raw
		unrealizedSats = trackedNow - remainingBasisSats
	}

	const disclosure: string[] = []
	if (qtyBoughtExact > 0n) {
		disclosure.push(`${whole(qtyBoughtExact)} bought here`)
	}
	const boughtUnpriced = big(t.qty_bought_unpriced)
	if (boughtUnpriced > 0n) {
		disclosure.push(`${whole(boughtUnpriced)} bought token-for-token`)
	}
	const minted = big(t.qty_minted)
	if (minted > 0n) {
		disclosure.push(`${whole(minted)} minted`)
	}
	const transferredIn = big(t.qty_transferred_in)
	if (transferredIn > 0n) {
		disclosure.push(`${whole(transferredIn)} transferred in`)
	}
	const lpOut = big(t.qty_lp_out)
	if (lpOut > 0n) {
		disclosure.push(`${whole(lpOut)} from LP`)
	}

	const valuedQty = big(t.qty_valued_usd)
	const unvaluedQty = big(t.qty_unvalued)
	if (t.valued_usd_acquired !== null && valuedQty > 0n) {
		disclosure.push(
			`${whole(valuedQty)} valued ~$${t.valued_usd_acquired.toLocaleString('en-US', {
				maximumFractionDigits: 2
			})} at receipt`
		)
	}
	if (unvaluedQty > 0n) {
		disclosure.push(`${whole(unvaluedQty)} with no price coverage`)
	}
	if (t.coverage === 'truncated') {
		disclosure.push('history truncated')
	}
	if (t.reconciled === false) {
		disclosure.push('balance does not fully reconcile')
	}

	return {
		investedSats,
		remainingBasisSats,
		avgEntrySatsPerToken: t.avg_entry_sats_per_token,
		realizedSats: big(t.realized_sats),
		unrealizedSats,
		trackedQty,
		disclosure,
		reconciled: t.reconciled,
		truncated: t.coverage === 'truncated',
		valuedUsdAcquired: t.valued_usd_acquired,
		valuedQty,
		unvaluedQty
	}
}
